import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  listAllSearchDomains,
  listDomains,
  listPackets,
  type SearchDomain,
  type WhitelistedDomain,
  type PacketSummary,
} from '../../services/api';

interface AdminDataContextValue {
  searchDomains: SearchDomain[];
  tenants: WhitelistedDomain[];
  packets: PacketSummary[];
  refreshSearchDomains: () => Promise<void>;
  refreshTenants: () => Promise<void>;
  refreshPackets: () => Promise<void>;
  loading: boolean;
}

const AdminDataContext = createContext<AdminDataContextValue>({
  searchDomains: [],
  tenants: [],
  packets: [],
  refreshSearchDomains: async () => {},
  refreshTenants: async () => {},
  refreshPackets: async () => {},
  loading: true,
});

export function AdminDataProvider({ children }: { children: React.ReactNode }) {
  const [searchDomains, setSearchDomains] = useState<SearchDomain[]>([]);
  const [tenants, setTenants] = useState<WhitelistedDomain[]>([]);
  const [packets, setPackets] = useState<PacketSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSearchDomains = useCallback(async () => {
    try {
      setSearchDomains(await listAllSearchDomains());
    } catch { /* silent */ }
  }, []);

  const refreshTenants = useCallback(async () => {
    try {
      setTenants(await listDomains());
    } catch { /* silent */ }
  }, []);

  const refreshPackets = useCallback(async () => {
    try {
      setPackets(await listPackets());
    } catch { /* silent */ }
  }, []);

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    Promise.all([refreshSearchDomains(), refreshTenants(), refreshPackets()])
      .finally(() => setLoading(false));
  }, [refreshSearchDomains, refreshTenants, refreshPackets]);

  return (
    <AdminDataContext.Provider value={{
      searchDomains, tenants, packets,
      refreshSearchDomains, refreshTenants, refreshPackets,
      loading,
    }}>
      {children}
    </AdminDataContext.Provider>
  );
}

export function useAdminData() {
  return useContext(AdminDataContext);
}
